import { useCallback, useContext, useRef } from 'react';
import { MergeDuplicateMetricsContext } from './MergeDuplicateMetrics';
import useDuplicationWarning from '../../MetricPopup/MetricForm/NormalMetricForm/useIsDraftADuplicate';
import MetricUsageLookupContext from '../../../../../contexts/MetricUsageLookupContext';
import useFindAndReplace from '../FindAndReplace/useFindAndReplace';
import { FindAndReplace } from '../types';
import getTimeStamp from '../../../../../getTimeStamp';
import getIdentifier from '../../../../../getIdentifier';
import CurrentUserContext from '../../../../../contexts/CurrentUserContext';

const useMergeDuplicateMetrics = () => {
  const { primaryMetric } = useContext(MergeDuplicateMetricsContext);
  const { metricLookup } = useContext(MetricUsageLookupContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  let metricReceipt = useRef<FindAndReplace.MetricReceipt | null>(null);

  const duplicates = useDuplicationWarning({
    metricDraft: primaryMetric,
  }).duplicates.filter((m) => m.status !== 'archived');
  const findAndReplace = useFindAndReplace();

  const mergeDuplicateMetrics = useCallback(async () => {
    const promises = duplicates.map(async (duplicateMetric) => {
      const metricUsage = metricLookup[duplicateMetric.id];

      if (!metricUsage || !primaryMetric?.id) return;

      metricReceipt.current = {
        replaceMetricId: primaryMetric.id,
        findMetricId: duplicateMetric.id,
        createdOn: getTimeStamp(),
        createdBy: currentUserId,
        id: getIdentifier(),
        affectedContentIds: {
          boards: metricUsage.boards.map((b) => b.id),
          goals: metricUsage.goals.map((g) => g.id),
          scorecards: metricUsage.scorecards.map((s) => s.id),
          dashboardGadgets: metricUsage.dashboardGadgets.map((d) => d.gadgetId),
          chartDefinitions: metricUsage.chartDefinitions.map((c) => c.id),
          compoundMetrics: metricUsage.compoundMetrics.map((c) => c.id),
        },
      };

      return findAndReplace(metricReceipt.current, true);
    });
    await Promise.all(promises);
  }, [currentUserId, duplicates, findAndReplace, metricLookup, primaryMetric]);

  return mergeDuplicateMetrics;
};

export default useMergeDuplicateMetrics;
